import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import Button from './Button';
import Share from './Share';
import MondialiSectionLayout from './MondialiSectionLayout';
import { Skeleton, SkeletonCircle, SkeletonText } from './Skeleton';
import {
  getMondialiRegistrations,
  getMondialiRegistrationsSummary,
} from '../api/mondiali';
import { useUserProfile } from '../contexts/UserProfileContext';
import {
  MONDIALI_HOME_PATH,
  MONDIALI_FINALE_PATH,
  MONDIALI_PREMI_PATH,
  MONDIALI_ROSTERS_PATH,
  MONDIALI_TOP_PLAYER_PATH,
  getMondialiCompetitionSummaryStage,
  getMondialiWeekLabel,
} from '../utils/mondialiRouting';
import {
  MONDIALI_RESULTS_SHARE_ERROR_MESSAGE,
  MONDIALI_RESULTS_SHARE_SUCCESS_MESSAGE,
  MONDIALI_SHARE_ERROR_MESSAGE,
  MONDIALI_SHARE_SUCCESS_MESSAGE,
  MONDIALI_SHARE_TEXT,
  MONDIALI_SHARE_TITLE,
  getMondialiResultsShareText,
  getMondialiShareUrl,
  showMondialiShareSnackbar,
} from '../utils/mondialiSharing';
import buttonStyles from '../styles/Button.module.css';
import styles from '../styles/Mondiali.module.css';
import 'flag-icons/css/flag-icons.min.css';

const MONDIALI_STADIUM_IMAGE_URL = `${process.env.PUBLIC_URL || ''}/images/mondiali_bg.jpeg`;
const MONDIALI_PROFILE_CACHE_KEY = 'mondiali-profile-snapshot';
const MondialiShellContext = createContext(false);

const sortByUsername = (a, b) => a.username.localeCompare(b.username, 'it', { sensitivity: 'base' });
const sortByCountryName = (a, b) => a.country_name_it.localeCompare(b.country_name_it, 'it', { sensitivity: 'base' });

const createDemoPlayers = (countryAlpha2, countryNameIt, prefix, total) =>
  Array.from({ length: total }, (_, index) => ({
    username: `${prefix}${String(index + 1).padStart(2, '0')}`,
    country_alpha2: countryAlpha2,
    country_name_it: countryNameIt,
  }));

const DEMO_REGISTRATIONS = [
  ...createDemoPlayers('IT', 'Italia', 'azzurro', 40),
  ...createDemoPlayers('BR', 'Brasile', 'verdeoro', 40),
  ...createDemoPlayers('AR', 'Argentina', 'albiceleste', 2),
  ...createDemoPlayers('CA', 'Canada', 'maple', 2),
  ...createDemoPlayers('CH', 'Svizzera', 'helvetic', 2),
  ...createDemoPlayers('ES', 'Spagna', 'iberia', 2),
  ...createDemoPlayers('JP', 'Giappone', 'sakura', 2),
  ...createDemoPlayers('MA', 'Marocco', 'atlas', 1),
  ...createDemoPlayers('NO', 'Norvegia', 'fjord', 1),
  ...createDemoPlayers('NZ', 'Nuova Zelanda', 'kiwi', 1),
  ...createDemoPlayers('QA', 'Qatar', 'dune', 1),
];

const getDemoRegistrationSummary = () => ({
  playersCount: DEMO_REGISTRATIONS.length,
  countriesCount: new Set(DEMO_REGISTRATIONS.map((entry) => entry.country_alpha2)).size,
});

const readCachedMondialiProfile = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(MONDIALI_PROFILE_CACHE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }

    return {
      is_registered: parsed.is_registered === true,
      username: typeof parsed.username === 'string' ? parsed.username : null,
      country_alpha2: typeof parsed.country_alpha2 === 'string' ? parsed.country_alpha2 : null,
      competition_stelline_total: Number.isFinite(Number(parsed.competition_stelline_total))
        ? Number(parsed.competition_stelline_total)
        : null,
    };
  } catch (_) {
    return null;
  }
};

const writeCachedMondialiProfile = (mondialiProfile) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    if (!mondialiProfile || typeof mondialiProfile !== 'object') {
      window.localStorage.removeItem(MONDIALI_PROFILE_CACHE_KEY);
      return;
    }

    window.localStorage.setItem(MONDIALI_PROFILE_CACHE_KEY, JSON.stringify({
      is_registered: mondialiProfile.is_registered === true,
      username: typeof mondialiProfile.username === 'string' ? mondialiProfile.username : null,
      country_alpha2: typeof mondialiProfile.country_alpha2 === 'string' ? mondialiProfile.country_alpha2 : null,
      competition_stelline_total: Number.isFinite(Number(mondialiProfile.competition_stelline_total))
        ? Number(mondialiProfile.competition_stelline_total)
        : null,
    }));
  } catch (_) {
  }
};

const buildRegistrationsByCountry = (registrations) => {
  const grouped = new Map();

  registrations.forEach((entry) => {
    const key = entry.country_alpha2;
    const existing = grouped.get(key);
    if (existing) {
      existing.players.push(entry);
    } else {
      grouped.set(key, {
        country_alpha2: entry.country_alpha2,
        country_name_it: entry.country_name_it,
        players: [entry],
      });
    }
  });

  return Array.from(grouped.values())
    .map((group) => ({
      ...group,
      players: [...group.players].sort(sortByUsername),
    }))
    .sort(sortByCountryName);
};

export const getCompetitionStatusMessage = (now = new Date()) => {
  const summaryStage = getMondialiCompetitionSummaryStage(now);

  if (summaryStage === 'pre_opening') {
    return "La competizione inizia l'11 giugno con la Cerimonia di Apertura. Invita i tuoi amici ad unirsi alla tua squadra.";
  }

  if (summaryStage === 'warmup') {
    return "Ci siamo! Le porte sono ufficialmente aperte! Dalle 00.00 UTC del 14 giugno ogni stellina conquistata guiderà il tuo Paese verso la gloria!";
  }

  if (summaryStage === 'live') {
    return "La competizione è entrata nel vivo! Le iscrizioni sono ancora aperte: gioca e invita i tuoi amici per portare il tuo Paese sul podio!";
  }

  return "Triplice fischio: il campo ha emesso i suoi verdetti, è il tempo delle premiazioni! Dopo aver contato ogni singola stellina, siamo pronti ad incoronare i nostri campioni! Complimenti ai vincitori e alle vincitrici per la performance straordinaria e a tutti i giocatori per l’impegno sul campo, siete stati fenomenali, grazie mille!";
};

export const getCompetitionSummaryText = (summaryCounts, now = new Date()) => {
  const safePlayersCount = Number(summaryCounts?.playersCount) || 0;
  const safeCountriesCount = Number(summaryCounts?.countriesCount) || 0;
  const summaryStage = getMondialiCompetitionSummaryStage(now);

  if (summaryStage === 'pre_opening') {
    return `Al momento ci sono ${safePlayersCount} giocatori registrati da ${safeCountriesCount} Paesi diversi. ${getCompetitionStatusMessage(now)}`;
  }

  if (summaryStage === 'warmup') {
    return `Ci siamo! Le porte sono ufficialmente aperte! ${safePlayersCount} giocatori da ${safeCountriesCount} Paesi diversi sono già in campo per il riscaldamento. Dalle 00.00 UTC del 14 giugno ogni stellina conquistata guiderà il tuo Paese verso la gloria!`;
  }

  if (summaryStage === 'live') {
    return `La competizione è entrata nel vivo; ${safePlayersCount} giocatori da ${safeCountriesCount} Paesi diversi stanno regalando colpi di scena incredibili, con continui ribaltoni nelle Top 11 dei Paesi in gara! Le iscrizioni sono ancora aperte! Gioca e invita i tuoi amici per portare il tuo Paese sul podio!`;
  }

  return "Triplice fischio: il campo ha emesso i suoi verdetti, è il tempo delle premiazioni! Dopo aver contato ogni singola stellina, siamo pronti ad incoronare i nostri campioni! Complimenti ai vincitori e alle vincitrici per la performance straordinaria e a tutti i giocatori per l’impegno sul campo, siete stati fenomenali, grazie mille!";
};

export const MondialiRostersContent = ({ isLoading, byCountry }) => {
  if (isLoading) {
    return (
      <div className={styles.rostersSkeletonGrid} aria-hidden="true">
        {Array.from({ length: 5 }).map((_, cardIndex) => (
          <article key={`rosters-skeleton-${cardIndex}`} className={styles.rostersSkeletonCard}>
            <div className={styles.rostersSkeletonHeader}>
              <SkeletonCircle size={18} />
              <Skeleton width="42%" height="1.05rem" borderRadius="6px" />
            </div>
            <div className={styles.rostersSkeletonList}>
              {Array.from({ length: 8 }).map((__, lineIndex) => (
                <SkeletonText
                  key={`rosters-skeleton-line-${cardIndex}-${lineIndex}`}
                  lines={1}
                  width={lineIndex % 3 === 0 ? '72%' : lineIndex % 3 === 1 ? '86%' : '64%'}
                />
              ))}
            </div>
          </article>
        ))}
      </div>
    );
  }

  return (
    <div className={styles.countryGrid}>
      {byCountry.map((group) => (
        <article key={group.country_alpha2} className={styles.countryCard}>
          <h3 className={styles.countryHeader}>
            <span className={styles.flagFrame} aria-hidden="true">
              <span className={`fi fi-${group.country_alpha2.toLowerCase()} ${styles.flag}`} />
            </span>
            <span>{group.country_name_it}</span>
          </h3>
          <ul className={styles.countryList}>
            {group.players.map((entry) => (
              <li key={`${group.country_alpha2}-${entry.username}`} className={styles.listItem}>
                {entry.username}
              </li>
            ))}
          </ul>
        </article>
      ))}
    </div>
  );
};

export const MondialiHomePageShell = ({
  pageTitle,
  summaryTitle,
  summaryText,
  summaryActions,
  activePath,
  now,
  children,
  bodyClassName = 'mondiali-home-page',
  stadiumImageUrl = MONDIALI_STADIUM_IMAGE_URL,
}) => {
  return (
    <MondialiSectionLayout
      pageTitle={pageTitle}
      summaryTitle={summaryTitle}
      summaryText={summaryText}
      summaryActions={summaryActions}
      activePath={activePath}
      now={now}
      bodyClassName={bodyClassName}
      stadiumImageUrl={stadiumImageUrl}
    >
      {children}
    </MondialiSectionLayout>
  );
};

const MondialiPersistentShellContent = ({
  pageTitle,
  activePath,
  now = null,
  children,
  bodyClassName = 'mondiali-home-page',
  stadiumImageUrl = MONDIALI_STADIUM_IMAGE_URL,
}) => {
  const navigate = useNavigate();
  const { profile, isLoading: isProfileLoading } = useUserProfile();
  const [cachedMondialiProfile, setCachedMondialiProfile] = useState(() => readCachedMondialiProfile());
  const [summaryCounts, setSummaryCounts] = useState({ playersCount: 0, countriesCount: 0 });
  const [isSummaryLoading, setIsSummaryLoading] = useState(true);
  const isDemoMode = useMemo(() => new URLSearchParams(window.location.search).get('demo') === '1', []);
  const pageNow = useMemo(() => now || new Date(), [now]);
  const effectiveMondialiProfile = profile?.mondiali || cachedMondialiProfile || null;
  const hasOptimisticMondialiProfile = cachedMondialiProfile !== null;
  const isMondialiRegistered = Boolean(effectiveMondialiProfile?.is_registered);
  const summaryStage = useMemo(() => getMondialiCompetitionSummaryStage(pageNow), [pageNow]);
  const isPostCompetitionStage = summaryStage === 'final';
  const canRenderSummaryActions = !isProfileLoading || hasOptimisticMondialiProfile;
  const competitionStellineTotal = Number.isFinite(Number(effectiveMondialiProfile?.competition_stelline_total))
    ? Number(effectiveMondialiProfile.competition_stelline_total)
    : null;

  useEffect(() => {
    if (profile?.mondiali && typeof profile.mondiali === 'object') {
      writeCachedMondialiProfile(profile.mondiali);
      setCachedMondialiProfile(readCachedMondialiProfile());
      return;
    }

    if (!isProfileLoading && profile && profile.mondiali === null) {
      writeCachedMondialiProfile(null);
      setCachedMondialiProfile(null);
    }
  }, [isProfileLoading, profile]);

  useEffect(() => {
    if (isDemoMode) {
      setSummaryCounts(getDemoRegistrationSummary());
      setIsSummaryLoading(false);
      return;
    }

    setIsSummaryLoading(true);

    const loadSummary = async () => {
      try {
        const payload = await getMondialiRegistrationsSummary();
        setSummaryCounts({
          playersCount: Number(payload?.players_count) || 0,
          countriesCount: Number(payload?.countries_count) || 0,
        });
      } catch (_) {
        setSummaryCounts({ playersCount: 0, countriesCount: 0 });
      } finally {
        setIsSummaryLoading(false);
      }
    };

    loadSummary();
  }, [isDemoMode]);

  const handleStartTraining = () => {
    navigate('/');
  };

  const firstRegisteredActionLabel = (() => {
    if (summaryStage === 'warmup') {
      return 'Inizia a giocare';
    }

    if (summaryStage === 'live') {
      return 'Vai ai giochi';
    }

    if (summaryStage === 'final') {
      return 'Continua a giocare';
    }

    return 'Inizia a giocare';
  })();

  const secondRegisteredActionConfig = (() => {
    if (summaryStage === 'final') {
      return {
        buttonLabel: 'Condividi il tuo risultato',
        text: getMondialiResultsShareText(competitionStellineTotal),
        url: getMondialiShareUrl(MONDIALI_HOME_PATH),
        successMessage: MONDIALI_RESULTS_SHARE_SUCCESS_MESSAGE,
        errorMessage: MONDIALI_RESULTS_SHARE_ERROR_MESSAGE,
      };
    }

    return {
      buttonLabel: 'Invita amici',
      text: MONDIALI_SHARE_TEXT,
      url: getMondialiShareUrl(),
      successMessage: MONDIALI_SHARE_SUCCESS_MESSAGE,
      errorMessage: MONDIALI_SHARE_ERROR_MESSAGE,
    };
  })();

  const summaryActions = !canRenderSummaryActions ? null : (isMondialiRegistered ? (
    <div className={styles.ctaRow}>
      <Button secondary className={styles.ctaActionButton} onClick={handleStartTraining}>
        {firstRegisteredActionLabel}
      </Button>
      <Share
        title={MONDIALI_SHARE_TITLE}
        text={secondRegisteredActionConfig.text}
        url={secondRegisteredActionConfig.url}
        className={`${buttonStyles.primaryBlue} ${styles.ctaActionButton}`}
        buttonLabel={secondRegisteredActionConfig.buttonLabel}
        onShareSuccess={() => showMondialiShareSnackbar(secondRegisteredActionConfig.successMessage)}
        onShareError={() => showMondialiShareSnackbar(secondRegisteredActionConfig.errorMessage, 'error')}
      />
    </div>
  ) : (
    <div className={styles.ctaRow}>
      <Button secondary className={styles.ctaActionButton} onClick={handleStartTraining}>
        {isPostCompetitionStage ? 'Continua a giocare' : firstRegisteredActionLabel}
      </Button>
    </div>
  ));

  const summaryText = useMemo(
    () => getCompetitionSummaryText(summaryCounts, pageNow),
    [pageNow, summaryCounts],
  );
  const summaryTextContent = isSummaryLoading ? (
    <div aria-hidden="true" data-testid="mondiali-summary-skeleton">
      <div className={styles.summaryTextSkeleton}>
        <Skeleton width="100%" height="0.95rem" borderRadius="999px" />
        <Skeleton width="94%" height="0.95rem" borderRadius="999px" />
        <Skeleton width="72%" height="0.95rem" borderRadius="999px" />
      </div>
    </div>
  ) : summaryText;

  return (
    <MondialiHomePageShell
      pageTitle={pageTitle}
      summaryTitle="ALMA Giochini Mondiali"
      summaryText={summaryTextContent}
      summaryActions={summaryActions}
      activePath={activePath}
      now={pageNow}
      bodyClassName={bodyClassName}
      stadiumImageUrl={stadiumImageUrl}
    >
      {children}
    </MondialiHomePageShell>
  );
};

export const MondialiSectionPageShell = ({
  pageTitle,
  activePath,
  now = null,
  children,
  bodyClassName = 'mondiali-home-page',
  stadiumImageUrl = MONDIALI_STADIUM_IMAGE_URL,
}) => {
  return (
    <MondialiShellContext.Provider value={true}>
      <MondialiPersistentShellContent
        pageTitle={pageTitle}
        activePath={activePath}
        now={now}
        bodyClassName={bodyClassName}
        stadiumImageUrl={stadiumImageUrl}
      >
        {children}
      </MondialiPersistentShellContent>
    </MondialiShellContext.Provider>
  );
};

export const useIsInsideMondialiShell = () => useContext(MondialiShellContext);

const getMondialiShellRouteMeta = (pathname) => {
  if (pathname === MONDIALI_ROSTERS_PATH) {
    return {
      pageTitle: 'ALMA Giochini Mondiali | Giochini di Parole',
      activePath: MONDIALI_ROSTERS_PATH,
    };
  }

  if (pathname === MONDIALI_PREMI_PATH) {
    return {
      pageTitle: 'Premi Mondiali | ALMA Giochini Mondiali | Giochini di Parole',
      activePath: MONDIALI_PREMI_PATH,
    };
  }

  if (pathname === MONDIALI_TOP_PLAYER_PATH) {
    return {
      pageTitle: 'Top Player Mondiali | ALMA Giochini Mondiali | Giochini di Parole',
      activePath: MONDIALI_TOP_PLAYER_PATH,
    };
  }

  if (pathname === MONDIALI_FINALE_PATH) {
    return {
      pageTitle: 'Finale Mondiali | ALMA Giochini Mondiali | Giochini di Parole',
      activePath: MONDIALI_FINALE_PATH,
    };
  }

  const weeklyMatch = pathname.match(/^\/mondiali\/classifica\/(\d+)$/);
  if (weeklyMatch) {
    const weekNumber = Number.parseInt(weeklyMatch[1], 10);
    if (!Number.isInteger(weekNumber) || weekNumber <= 0) {
      return {
        pageTitle: 'ALMA Giochini Mondiali | Giochini di Parole',
        activePath: pathname,
      };
    }
    return {
      pageTitle: `${getMondialiWeekLabel(weekNumber)} | ALMA Giochini Mondiali | Giochini di Parole`,
      activePath: pathname,
    };
  }

  return {
    pageTitle: 'ALMA Giochini Mondiali | Giochini di Parole',
    activePath: null,
  };
};

export const MondialiTabbedLayout = () => {
  const location = useLocation();
  const routeMeta = useMemo(
    () => getMondialiShellRouteMeta(location.pathname),
    [location.pathname],
  );

  return (
    <MondialiSectionPageShell
      pageTitle={routeMeta.pageTitle}
      activePath={routeMeta.activePath}
      now={new Date()}
      bodyClassName="mondiali-home-page"
    >
      <Outlet />
    </MondialiSectionPageShell>
  );
};

const MondialiHomePage = () => {
  const isInsideMondialiShell = useIsInsideMondialiShell();
  const [isLoading, setIsLoading] = useState(true);
  const [registrations, setRegistrations] = useState([]);
  const isDemoMode = useMemo(() => new URLSearchParams(window.location.search).get('demo') === '1', []);

  useEffect(() => {
    if (isDemoMode) {
      setRegistrations(DEMO_REGISTRATIONS);
      setIsLoading(false);
      return;
    }

    const loadRosters = async () => {
      try {
        const payload = await getMondialiRegistrations();
        setRegistrations(payload?.registrations || []);
      } catch (_) {
        setRegistrations([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadRosters();
  }, [isDemoMode]);

  const byCountry = useMemo(() => buildRegistrationsByCountry(registrations), [registrations]);
  const content = <MondialiRostersContent isLoading={isLoading} byCountry={byCountry} />;

  if (isInsideMondialiShell) {
    return content;
  }

  return (
    <MondialiSectionPageShell
      pageTitle="ALMA Giochini Mondiali | Giochini di Parole"
      activePath={MONDIALI_ROSTERS_PATH}
      bodyClassName="mondiali-home-page"
    >
      {content}
    </MondialiSectionPageShell>
  );
};

export default MondialiHomePage;
